import React, { useEffect, useState } from 'react';
import type { UseFormRegister, FieldErrors, UseFormSetValue } from 'react-hook-form';
import type { FormData } from './CalculationFormData';

interface AdvancedOptionsSectionProps {
  register: UseFormRegister<FormData>;
  errors: FieldErrors<FormData>;
  watchedValues: FormData;
  isCalculating: boolean;
  setValue?: UseFormSetValue<FormData>;
}

export default function AdvancedOptionsSection({
  register,
  errors,
  watchedValues,
  isCalculating,
  setValue
}: AdvancedOptionsSectionProps) {

  // State to remember both pension values
  const [pensionPercentageValue, setPensionPercentageValue] = useState('10');
  const [pensionFixedValue, setPensionFixedValue] = useState('');
  const [lastPensionType, setLastPensionType] = useState(watchedValues.pensionType);

  // Handle pension type switching and restore appropriate values
  useEffect(() => {
    if (setValue && watchedValues.hasPension && watchedValues.pensionType !== lastPensionType) {
      // Store current value before switching
      if (lastPensionType === 'percentage' && watchedValues.pensionAmount) {
        setPensionPercentageValue(watchedValues.pensionAmount);
      } else if (lastPensionType === 'fixed' && watchedValues.pensionAmount) {
        setPensionFixedValue(watchedValues.pensionAmount);
      }
      
      // Set the appropriate value for the new type
      if (watchedValues.pensionType === 'percentage') {
        setValue('pensionAmount', pensionPercentageValue);
      } else if (watchedValues.pensionType === 'fixed') {
        setValue('pensionAmount', pensionFixedValue);
      }
      
      setLastPensionType(watchedValues.pensionType);
    }
  }, [watchedValues.pensionType, watchedValues.hasPension, setValue, pensionPercentageValue, pensionFixedValue, lastPensionType, watchedValues.pensionAmount]);

  // Set default pension percentage when first enabling pension
  useEffect(() => {
    if (setValue && watchedValues.hasPension && watchedValues.pensionType === 'percentage' && !watchedValues.pensionAmount) {
      setValue('pensionAmount', pensionPercentageValue);
    }
  }, [watchedValues.hasPension, watchedValues.pensionType, watchedValues.pensionAmount, setValue, pensionPercentageValue]);
  
  const formatCurrencyInput = (value: string): string => {
    const numericValue = value.replace(/[^\d]/g, '');
    if (!numericValue) return '';
    
    const formatted = parseInt(numericValue).toLocaleString('en-ZA');
    return `R ${formatted}`;
  };

  const handleCurrencyInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formattedValue = formatCurrencyInput(rawValue);
    e.target.value = formattedValue;
  };

  if (!watchedValues.showAdvancedOptions) {
    return null;
  }

  return (
    <div className="border-t border-gray-200 dark:border-slate-700 pt-6 space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-4">Advanced Tax Options</h3>
        <p className="text-sm text-gray-600 dark:text-slate-400 mb-6">
          Include additional tax considerations for a more accurate calculation
        </p>
      </div>

      {/* Medical Aid Section */}
      <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="hasMedicalAid"
            {...register('hasMedicalAid')}
            className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 rounded focus:ring-sa-green"
            disabled={isCalculating}
          />
          <label htmlFor="hasMedicalAid" className="ml-2 text-sm font-medium text-gray-700 dark:text-slate-300">
            Medical Aid Contributions
          </label>
        </div>
        
        {watchedValues.hasMedicalAid && (
          <div className="ml-6 space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="medicalAidMainMember"
                {...register('medicalAidMainMember')}
                className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 rounded focus:ring-sa-green"
                disabled={isCalculating}
              />
              <label htmlFor="medicalAidMainMember" className="ml-2 text-sm text-gray-700 dark:text-slate-300">
                I am the main member (R347/month tax credit)
              </label>
            </div>
            
            <div>
              <label htmlFor="medicalAidDependents" className="block text-sm text-gray-700 dark:text-slate-300 mb-1">
                Number of dependents
              </label>
              <select
                id="medicalAidDependents"
                {...register('medicalAidDependents')}
                className="input-field w-20"
                disabled={isCalculating}
              >
                {Array.from({length: 11}, (_, i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                Each dependent: R234/month tax credit
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Pension Section */}
      <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="hasPension"
            {...register('hasPension')}
            className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 rounded focus:ring-sa-green"
            disabled={isCalculating}
          />
          <label htmlFor="hasPension" className="ml-2 text-sm font-medium text-gray-700 dark:text-slate-300">
            Pension/Retirement Fund Contributions
          </label>
        </div>
        
        {watchedValues.hasPension && (
          <div className="ml-6 space-y-4">
            <div>
              <label className="block text-sm text-gray-700 dark:text-slate-300 mb-2">Contribution Type</label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="percentage"
                    {...register('pensionType')}
                    className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 focus:ring-sa-green"
                    disabled={isCalculating}
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-slate-300">Percentage of salary</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="fixed"
                    {...register('pensionType')}
                    className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 focus:ring-sa-green"
                    disabled={isCalculating}
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-slate-300">Fixed amount</span>
                </label>
              </div>
            </div>
            
            <div>
              <label htmlFor="pensionAmount" className="block text-sm text-gray-700 dark:text-slate-300 mb-1">
                {watchedValues.pensionType === 'percentage' 
                  ? 'Percentage (%)' 
                  : watchedValues.incomeFrequency === 'monthly' 
                    ? 'Monthly amount' 
                    : 'Annual amount'}
              </label>
              <input
                type="text"
                id="pensionAmount"
                {...register('pensionAmount', {
                  validate: (value) => {
                    if (!watchedValues.hasPension || !value) return true;
                    
                    const numericValue = watchedValues.pensionType === 'percentage' 
                      ? parseFloat(value) 
                      : parseFloat(value.replace(/[^\d.]/g, ''));
                    
                    if (isNaN(numericValue) || numericValue < 0) {
                      return 'Please enter a valid amount';
                    }
                    
                    if (watchedValues.pensionType === 'percentage' && numericValue > 27.5) {
                      return 'Maximum tax-deductible percentage is 27.5%';
                    }
                    
                    return true;
                  }
                })}
                onChange={watchedValues.pensionType === 'fixed' ? handleCurrencyInput : undefined}
                placeholder={watchedValues.pensionType === 'percentage' 
                  ? '' 
                  : ''}
                className={`input-field w-full ${errors.pensionAmount ? 'border-red-300 dark:border-red-500' : ''}`}
                disabled={isCalculating}
              />
              {errors.pensionAmount && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.pensionAmount.message}</p>
              )}
              <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                Tax deductible up to 27.5% of income (max R350,000/year)
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Travel Allowance Section */}
      <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="hasTravelAllowance"
            {...register('hasTravelAllowance')}
            className="h-4 w-4 text-sa-green border-gray-300 dark:border-slate-600 rounded focus:ring-sa-green"
            disabled={isCalculating}
          />
          <label htmlFor="hasTravelAllowance" className="ml-2 text-sm font-medium text-gray-700 dark:text-slate-300">
            Company Vehicle / Travel Allowance
          </label>
        </div>
        
        {watchedValues.hasTravelAllowance && (
          <div className="ml-6 space-y-4">
            <div>
              <label htmlFor="vehicleValue" className="block text-sm text-gray-700 dark:text-slate-300 mb-1">
                Vehicle value (for fringe benefit calculation)
              </label>
              <input
                type="text"
                id="vehicleValue"
                {...register('vehicleValue')}
                onChange={handleCurrencyInput}
                placeholder="R 300,000"
                className="input-field w-full"
                disabled={isCalculating}
              />
              <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                Determined value of company vehicle
              </p>
            </div>
            
            <div>
              <label htmlFor="privateUsePercentage" className="block text-sm text-gray-700 dark:text-slate-300 mb-1">
                Private use percentage (%)
              </label>
              <input
                type="number"
                id="privateUsePercentage"
                {...register('privateUsePercentage', {
                  min: { value: 0, message: 'Cannot be negative' },
                  max: { value: 100, message: 'Cannot exceed 100%' }
                })}
                placeholder="20"
                className={`input-field w-24 ${errors.privateUsePercentage ? 'border-red-300 dark:border-red-500' : ''}`}
                disabled={isCalculating}
                min="0"
                max="100"
              />
              {errors.privateUsePercentage && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.privateUsePercentage.message}</p>
              )}
              <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                Percentage of vehicle used for private purposes
              </p>
            </div>
            
            <div>
              <label htmlFor="travelAllowanceAmount" className="block text-sm text-gray-700 dark:text-slate-300 mb-1">
                Annual travel allowance amount
              </label>
              <input
                type="text"
                id="travelAllowanceAmount"
                {...register('travelAllowanceAmount')}
                onChange={handleCurrencyInput}
                placeholder="R 100,000"
                className="input-field w-full"
                disabled={isCalculating}
              />
              <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                Additional travel allowance received (generally taxable)
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}